import React, { useRef } from 'react'
import { useRouter } from 'next/router'

import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@wartek-id/tabs'
import { historyUtils } from '@wartek-id/toolbox-helper'

import AndroidHandler from 'utils/androidHandler'
import Header from 'app/InspirationPage/components/revamp/Header'
import PageHeader from 'app/InspirationPage/components/revamp/PageHeader'
import InspirationBlock from '../components/revamp/InspirationBlock'
import { useTogglesStore } from 'stores/toggles'
import IdePraktikHeader from 'app/InspirationPage/components/v2/IdePraktikHeader'
import PrefetchLink from 'components/PrefetchLink'
import CategoryBlockV2 from 'app/InspirationPage/components/v2/CategoryBlock'
import { PageContainer, DesktopPageHeader } from 'components/ResponsiveLayout'
import { useIdePraktikDesktopView } from 'app/InspirationPage/hooks/useIdePraktikDesktopView'
import {
  useClickTracker,
  usePageViewTracker,
  useScrollTracker,
} from 'utils/satu-event-tracker'

const HEADER_TITLE = 'Yuk, Temukan Inspirasi Mengajar Hari Ini!'
const HEADER_SUBTITLE =
  'Praktik baik terpilih untuk membantu Bapak/Ibu menghadirkan Pembelajaran yang bermakna, berkesadaran, dan menggembirakan bagi murid'
const TRACKER_SCREEN_NAME = 'CERITA_PRAKTIK'
const TRACKER_EVENT_NAME = 'IDE_PRAKTIK_CORE_PWA_EVENT'

const HELPDESK_LINK = 'https://pusatinformasi.guru.kemdikbud.go.id'

const InspirationRevamp = () => {
  const androidHandler = AndroidHandler()
  const router = useRouter()
  const scrollElement = useRef<HTMLDivElement>(null)
  const { isDesktop } = useIdePraktikDesktopView()
  const isIdePraktikV2Enabled = useTogglesStore(
    (state) => state.toggles?.['ide-praktik-v2-enabled']
  )

  const trackTabClick = useClickTracker({
    enabled: isIdePraktikV2Enabled,
    activityType: 'CLICK_TAB_VIDEO_INSPIRASI',
    eventName: TRACKER_EVENT_NAME,
    defaultAttributes: { screen_name: TRACKER_SCREEN_NAME },
  })

  usePageViewTracker({
    enabled: isIdePraktikV2Enabled,
    activityType: 'PAGE_VIEW_TAB_CERITA_PRAKTIK',
    eventName: TRACKER_EVENT_NAME,
    screenName: TRACKER_SCREEN_NAME,
    attributes: {
      is_webview: [androidHandler.enabled, 'bool'],
    },
  })

  useScrollTracker({
    enabled: isIdePraktikV2Enabled,
    activityType: 'SCROLL_TAB_CERITA_PRAKTIK',
    eventName: TRACKER_EVENT_NAME,
    scrollElement: isDesktop ? null : scrollElement,
    attributes: {
      screen_name: TRACKER_SCREEN_NAME,
    },
  })

  if (isDesktop && isIdePraktikV2Enabled) {
    return (
      <div className="min-h-screen bg-white">
        <DesktopPageHeader
          navLinks={[{ label: 'Bantuan', href: HELPDESK_LINK, external: true }]}
          breadcrumbItems={[{ label: 'Ide Praktik', href: null }]}
          breadcrumbClassName="bg-[#0036a3] !border-b-0"
          breadcrumbTextClassName="[&_a]:!text-white/70 [&_span]:!text-white"
          bottomBar={
            <>
              {/* Hero section */}
              <div className="bg-[#0036a3]">
                <PageContainer className="!py-6">
                  <div className="flex items-center gap-4">
                    <img
                      src="/icon/ide-praktik.svg"
                      alt=""
                      width={60}
                      height={60}
                    />
                    <div>
                      <h1 className="text-white text-lg font-semibold">
                        {HEADER_TITLE}
                      </h1>
                      <p className="text-white text-sm mt-1 opacity-90">
                        {HEADER_SUBTITLE}
                      </p>
                    </div>
                  </div>
                </PageContainer>
              </div>

              {/* Tabs */}
              <div className="bg-white border-b border-gray-200">
                <PageContainer className="!py-0">
                  <Tabs
                    withBorder
                    selectedIndex={0}
                    onChange={(index) => {
                      if (index !== 1) return
                      window.location.href = '/video-inspirasi'
                    }}
                  >
                    <TabList
                      style={{
                        borderBottom: '1px solid var(--color-border-subdued)',
                      }}
                    >
                      <Tab>Cerita Praktik</Tab>
                      <Tab
                        onClick={() => {
                          trackTabClick(undefined, { keepAlive: true })
                        }}
                      >
                        <PrefetchLink href="/video-inspirasi">
                          Video Inspirasi
                        </PrefetchLink>
                      </Tab>
                    </TabList>
                  </Tabs>
                </PageContainer>
              </div>
            </>
          }
        />
        <main>
          {/* Category badges */}
          <div className="bg-white">
            <PageContainer className="!py-0">
              <CategoryBlockV2 />
            </PageContainer>
          </div>

          {/* Content grid */}
          <div className="bg-surface-subdued">
            <PageContainer>
              <InspirationBlock isDesktop />
            </PageContainer>
          </div>
        </main>
      </div>
    )
  }

  if (isIdePraktikV2Enabled) {
    return (
      <>
        <style jsx global>
          {`
            @media (max-width: 767px) {
              .scrollbar-hide-auto {
                overflow-y: auto;
                scrollbar-width: thin;
                scrollbar-color: transparent transparent;
                transition: scrollbar-color 0.3s;
              }

              .scrollbar-hide-auto:hover {
                scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
              }

              /* Webkit */
              .scrollbar-hide-auto::-webkit-scrollbar {
                width: 8px;
              }

              .scrollbar-hide-auto::-webkit-scrollbar-track {
                background: transparent;
              }

              .scrollbar-hide-auto::-webkit-scrollbar-thumb {
                background: transparent;
                border-radius: 4px;
                transition: background 0.3s;
              }

              .scrollbar-hide-auto:hover::-webkit-scrollbar-thumb {
                background: rgba(0, 0, 0, 0.2);
              }

              .scrollbar-hide-auto::-webkit-scrollbar-thumb:hover {
                background: rgba(0, 0, 0, 0.4);
              }
            }
          `}
        </style>
        <div className="bg-white flex flex-col flex-1 h-full max-w-xl mx-auto">
          <IdePraktikHeader
            onBack={() => {
              historyUtils.backToApp(() => {
                const isNewActivity = window.history.length <= 2
                if (isNewActivity) router.push('/home')
                else router.back()
              })
            }}
            subtitle={HEADER_SUBTITLE}
            title={HEADER_TITLE}
          />
          <Tabs
            className="!flex !flex-col !flex-1 !min-h-0"
            isFitted
            withBorder
            selectedIndex={0}
            onChange={(index) => {
              if (index !== 1) return
              window.location.href = '/video-inspirasi'
            }}
          >
            <TabList
              className="flex-shrink-0"
              style={{ borderBottom: '1px solid var(--color-border-subdued)' }}
            >
              <Tab>Cerita Praktik</Tab>
              <Tab
                onClick={() => {
                  trackTabClick(undefined, { keepAlive: true })
                }}
              >
                <PrefetchLink
                  className="flex items-center justify-center w-full"
                  href="/video-inspirasi"
                >
                  Video Inspirasi
                </PrefetchLink>
              </Tab>
            </TabList>
            <TabPanels className="flex flex-col flex-1 min-h-0">
              <div
                ref={scrollElement}
                className="bg-white flex-1 overflow-y-auto scrollbar-hide-auto"
              >
                <TabPanel>
                  <CategoryBlockV2 />
                  <InspirationBlock />
                </TabPanel>
                <TabPanel>Video Inspirasi Konten</TabPanel>
              </div>
            </TabPanels>
          </Tabs>
        </div>
      </>
    )
  }

  return (
    <div className="max-w-xl mx-auto">
      <PageHeader />
      <div className="min-h-full w-full absolute h-full overflow-y-scroll overflow-x-hidden pb-14 mt-14">
        <Header />
        <InspirationBlock />
      </div>
    </div>
  )
}
export default InspirationRevamp
